import { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import axios from 'axios';
import * as AWSconst from '../../constants/AWS';
import * as API from '../../constants/API';
import { fromCognitoIdentityPool } from '@aws-sdk/credential-providers';
import { AwsCredentialIdentity } from '@aws-sdk/types';
import { SignatureV4 } from '@smithy/signature-v4';
import { HttpRequest } from '@smithy/protocol-http';
import { Sha256 } from '@aws-crypto/sha256-browser';
import { asyncGetSession } from '../Auth/api';
import {
  TrainingLog,
  TrainingLogCreateRequest,
  TrainingLogUpdateRequest,
  TrainingLogSummariesResponse,
  TrainingType,
  DominantHand,
} from '../../constants/TrainingLog';

/**
 * TrainingLog Admin API Client
 * Uses IAM SigV4 authentication for admin endpoints
 * Base URL: daycareApiRoot (includes /v2)
 */
export class TrainingLogApiClient {
  private client: AxiosInstance;
  private baseURL: string;

  static async ClientFactory(): Promise<TrainingLogApiClient> {
    const user = AWSconst.userPool().getCurrentUser();
    if (!user) {
      throw Error('user not signed in');
    }
    const session = await asyncGetSession(user);

    const region = process.env.REACT_APP_AWS_REGION || '';
    const identityPoolId = process.env.REACT_APP_AWS_IDENTITY_POOL_ID || '';

    // AWS SDK v3: Get credentials from Cognito Identity Pool
    const credentialsProvider = fromCognitoIdentityPool({
      identityPoolId,
      logins: {
        [AWSconst.cognitoIdpURL]: session.getIdToken().getJwtToken(),
      },
      clientConfig: { region },
    });

    const credentials = await credentialsProvider();

    return new TrainingLogApiClient(credentials, region);
  }

  constructor(credentials: AwsCredentialIdentity, region: string) {
    this.baseURL = API.daycareApiRoot;
    this.client = axios.create({
      baseURL: this.baseURL,
    });

    // Add AWS SigV4 signing interceptor
    this.client.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
      // Build full URL the same way Axios does (concatenation, not URL resolution)
      const fullUrl = (config.baseURL || '') + (config.url || '');
      const url = new URL(fullUrl);

      // Add query params from config.params (Axios adds these later, but we need them for signing)
      if (config.params) {
        Object.keys(config.params).forEach(key => {
          const value = config.params[key];
          if (value !== undefined && value !== null) {
            url.searchParams.append(key, String(value));
          }
        });
      }

      const method = config.method?.toUpperCase() || 'GET';

      // For GET/DELETE requests, body should be undefined
      // For POST/PUT, serialize body to JSON string (same as Axios does)
      let body: string | undefined;
      if (method !== 'GET' && method !== 'DELETE' && config.data !== undefined) {
        body = typeof config.data === 'string' ? config.data : JSON.stringify(config.data);
      }

      // Build headers for signing
      const headersRecord: Record<string, string> = {
        host: url.hostname,
      };

      // For POST/PUT requests, include content-type and content-length in signature
      if (method === 'POST' || method === 'PUT') {
        const contentType = config.headers?.get('content-type');
        if (contentType && typeof contentType === 'string') {
          headersRecord['content-type'] = contentType;
        }
        if (body !== undefined) {
          headersRecord['content-length'] = String(new TextEncoder().encode(body).length);
        }
      }

      if (config.headers) {
        Object.keys(config.headers).forEach(key => {
          const value = config.headers.get(key);
          if (value && typeof value === 'string') {
            const lowerKey = key.toLowerCase();
            if (lowerKey.startsWith('x-amz-')) {
              headersRecord[lowerKey] = value;
            }
          }
        });
      }

      // Build query parameters object for signing
      const queryParams: Record<string, string> = {};
      url.searchParams.forEach((value, key) => {
        queryParams[key] = value;
      });

      // Create HttpRequest for signing
      const httpRequestOptions: any = {
        method,
        protocol: url.protocol,
        hostname: url.hostname,
        port: url.port ? Number(url.port) : undefined,
        path: url.pathname,
        query: Object.keys(queryParams).length > 0 ? queryParams : undefined,
        headers: headersRecord,
      };

      if (body !== undefined) {
        httpRequestOptions.body = body;
      }

      const httpRequest = new HttpRequest(httpRequestOptions);

      // Sign the request
      const signer = new SignatureV4({
        credentials,
        region,
        service: 'execute-api',
        sha256: Sha256,
        uriEscapePath: true,
      });

      const signedRequest = await signer.sign(httpRequest, {
        signingDate: new Date(),
        signingRegion: region,
        signingService: 'execute-api',
        unsignableHeaders: new Set(['x-amz-content-sha256']),
      });

      // Apply signed headers to axios config
      const authHeader = signedRequest.headers['authorization'] || signedRequest.headers['Authorization'];
      const dateHeader = signedRequest.headers['x-amz-date'] || signedRequest.headers['X-Amz-Date'];
      const tokenHeader =
        signedRequest.headers['x-amz-security-token'] || signedRequest.headers['X-Amz-Security-Token'];

      if (authHeader) config.headers.set('authorization', authHeader);
      if (dateHeader) config.headers.set('x-amz-date', dateHeader);
      if (tokenHeader) config.headers.set('x-amz-security-token', tokenHeader);

      return config;
    });
  }

  /**
   * Helper to convert started_at to path parameter format
   * Replaces space with underscore: "2024-01-15 10:30:00" -> "2024-01-15_10:30:00"
   */
  private formatStartedAtForPath(startedAt: string): string {
    return startedAt.replace(' ', '_');
  }

  /**
   * Create a new training log
   * POST /admin/users/{user_id}/training_logs
   */
  async createTrainingLog(
    userId: string,
    data: TrainingLogCreateRequest,
    instructorName?: string,
  ): Promise<TrainingLog> {
    const params = instructorName ? { instructor_name: instructorName } : undefined;
    const response = await this.client.post(`/admin/users/${userId}/training_logs`, data, {
      headers: { 'content-type': 'application/json;charset=UTF-8' },
      params,
    });
    return response.data;
  }

  /**
   * Get the latest training log for a specific training type
   * GET /admin/users/{user_id}/training_logs/latest/{training_id}
   */
  async getLatestTrainingLog(userId: string, trainingId: number): Promise<TrainingLog> {
    const response = await this.client.get(`/admin/users/${userId}/training_logs/latest/${trainingId}`);
    return response.data;
  }

  /**
   * Get training log summaries for a specific month
   * GET /admin/users/{user_id}/training_logs/{training_type}/{year_month}/summaries
   */
  async getTrainingLogSummaries(
    userId: string,
    trainingType: TrainingType,
    yearMonth: string,
    dominant?: DominantHand,
  ): Promise<TrainingLogSummariesResponse> {
    const params = dominant ? { dominant } : undefined;
    const response = await this.client.get(
      `/admin/users/${userId}/training_logs/${trainingType}/${yearMonth}/summaries`,
      { params },
    );
    return response.data;
  }

  /**
   * Update a training log
   * PUT /admin/users/{user_id}/training_logs/record/{started_at}
   * Note: started_at path param uses underscore instead of space
   */
  async updateTrainingLog(userId: string, startedAt: string, data: TrainingLogUpdateRequest): Promise<void> {
    const formattedStartedAt = this.formatStartedAtForPath(startedAt);
    await this.client.put(`/admin/users/${userId}/training_logs/record/${formattedStartedAt}`, data, {
      headers: { 'content-type': 'application/json;charset=UTF-8' },
    });
  }

  /**
   * Delete a training log
   * DELETE /admin/users/{user_id}/training_logs/record/{started_at}
   * Note: started_at path param uses underscore instead of space
   */
  async deleteTrainingLog(userId: string, startedAt: string): Promise<void> {
    const formattedStartedAt = this.formatStartedAtForPath(startedAt);
    await this.client.delete(`/admin/users/${userId}/training_logs/record/${formattedStartedAt}`);
  }

  /**
   * Get training logs for a date range
   * GET /admin/users/{user_id}/training_logs/from/{from_date}/to/{to_date}
   * Returns array of TrainingLog with actual started_at timestamps (descending order)
   */
  async getTrainingLogs(userId: string, fromDate: string, toDate: string): Promise<TrainingLog[]> {
    const response = await this.client.get(`/admin/users/${userId}/training_logs/from/${fromDate}/to/${toDate}`);
    return response.data;
  }
}
